<template>
  <div class="flex w-full flex-col gap-11">
    <div class="flex flex-row items-center justify-between">
      <span class="text-3xl font-medium sm:text-5xl">{{
        __('Templates')
      }}</span>
      <Button
        class="hover-lift flex items-center gap-1.5"
        type="submit"
        transition="transition-all"
        @click="redirectToPage('redesign.company.templates.create')"
      >
        <span>
          {{ __('New Template') }}
        </span>
      </Button>
    </div>

    <div
      class="flex h-full w-full flex-col rounded-lg bg-white p-6 drop-shadow-md"
    >
      <div class="w-full">
        <InputText
            id="search"
            :placeholder="__('Search templates...')"
            :value="searchString"
            clearable
            @update:value="handleSearchInput"
        />
      </div>

      <div
        v-show="noValuesFound && !loading"
        class="mt-8 flex h-full w-full flex-1 items-center justify-center text-lg text-zinc-400"
      >
        {{ __('No templates found') }}
      </div>

      <div
        class="mt-8 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <button
          v-show="!noValuesFound"
          type="button"
          class="hover-lift flex min-h-[180px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label="Create new job template"
          @click="redirectToPage('redesign.company.templates.create')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
          >
            <mask
              id="mask0_5582_205"
              style="mask-type: alpha"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="25"
              height="24"
            >
              <rect x="0.5" width="24" height="24" class="fill-primary" />
            </mask>
            <g mask="url(#mask0_5582_205)">
              <path
                d="M11.5 13V16C11.5 16.2833 11.5958 16.5208 11.7875 16.7125C11.9792 16.9042 12.2167 17 12.5 17C12.7833 17 13.0208 16.9042 13.2125 16.7125C13.4042 16.5208 13.5 16.2833 13.5 16V13H16.5C16.7833 13 17.0208 12.9042 17.2125 12.7125C17.4042 12.5208 17.5 12.2833 17.5 12C17.5 11.7167 17.4042 11.4792 17.2125 11.2875C17.0208 11.0958 16.7833 11 16.5 11H13.5V8C13.5 7.71667 13.4042 7.47917 13.2125 7.2875C13.0208 7.09583 12.7833 7 12.5 7C12.2167 7 11.9792 7.09583 11.7875 7.2875C11.5958 7.47917 11.5 7.71667 11.5 8V11H8.5C8.21667 11 7.97917 11.0958 7.7875 11.2875C7.59583 11.4792 7.5 11.7167 7.5 12C7.5 12.2833 7.59583 12.5208 7.7875 12.7125C7.97917 12.9042 8.21667 13 8.5 13H11.5ZM5.5 21C4.95 21 4.47917 20.8042 4.0875 20.4125C3.69583 20.0208 3.5 19.55 3.5 19V5C3.5 4.45 3.69583 3.97917 4.0875 3.5875C4.47917 3.19583 4.95 3 5.5 3H19.5C20.05 3 20.5208 3.19583 20.9125 3.5875C21.3042 3.97917 21.5 4.45 21.5 5V19C21.5 19.55 21.3042 20.0208 20.9125 20.4125C20.5208 20.8042 20.05 21 19.5 21H5.5Z"
                class="fill-primary"
              />
            </g>
          </svg>

          <span class="text-primary">{{ __('New Template') }}</span>
        </button>

        <!-- Skeleton loader -->
        <template v-if="loading">
          <div
            v-for="i in 7"
            :key="`skeleton-${i}`"
            class="flex min-h-[180px] animate-pulse flex-col items-center justify-center gap-6 rounded-2xl border border-zinc-300 p-6"
            role="status"
            aria-label="Loading pipeline"
          >
            <div class="h-6 w-3/4 rounded bg-gray-200"></div>
            <div class="h-4 w-1/2 rounded bg-gray-200"></div>
            <div class="h-4 w-2/3 rounded bg-gray-200"></div>
          </div>
        </template>

        <!-- Template cards -->
        <div
          v-else
          v-for="template in templates.data"
          :key="template.id"
          class="hover-lift flex min-h-[180px] cursor-pointer flex-col items-center justify-between gap-4 rounded-2xl border border-zinc-300 p-5 transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          tabindex="0"
          role="button"
          :aria-label="`View template: ${template.name}`"
          @click="
            redirectToPage('redesign.company.templates.edit', template.id)
          "
          @keydown.enter="
            redirectToPage('redesign.company.templates.edit', template.id)
          "
          @keydown.space.prevent="
            redirectToPage('redesign.company..templates.edit', template.id)
          "
        >
          <div class="flex w-full flex-row items-start justify-between gap-x-2">
            <Icon
              name="work"
              class="rounded-md bg-secondary/10 px-2 py-1 !text-3xl"
            >
            </Icon>

            <div class="relative">
              <TemplatesActionsPopup
                :template-id="template.id"
                :is-open="openPopupMenuId === template.id"
                :is-loading-delete="isLoadingDelete"
                @edit="handleTemplateEdit"
                @delete="handleTemplateDelete"
                @toggle="handlePopupToggle(template.id)"
              />
            </div>
          </div>

          <div class="flex w-full flex-col items-start justify-center gap-2">
            <div class="w-full truncate font-medium">
              {{ template.name }}
            </div>
            <div class="w-full text-sm text-zinc-600">
              <span class="text-sm font-medium">{{ __('Edited on') }}: </span
              >{{ new Date(template.updated_at).toLocaleDateString('de-DE') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mt-auto">
        <Pagination
          v-if="templates.meta?.last_page > 1"
          :key="paginationKey"
          :current-page="templates.meta.current_page"
          :last-page="templates.meta.last_page"
          class="mt-6"
          @update:current-page="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import axios from 'axios';
  import { nextTick, onMounted, ref } from 'vue';
  import { useToast } from 'vue-toastification';

  import { __ } from '@/helpers';
  import Button from '@/components/block/button/button.vue';
  import Pagination from '@/components/global/Pagination.vue';
  import Icon from '@/components/global/Icon.vue';
  import TemplatesActionsPopup from './components/TemplatesActionsPopup.vue';
  import {InputText} from "@/components/form/input-text";

  const props = defineProps({
    data: {
      type: Object,
      required: true
    }
  });

  const toast = useToast();
  const templates = ref({});
  const paginationKey = ref(0);
  const searchString = ref('');
  const loading = ref(false);
  const openPopupMenuId = ref(null);
  let debounceTimeout;

  onMounted(async () => {
    await nextTick();
    const searchParams = new URL(window.location.href).searchParams.toString();
    await fetchTemplates(searchParams, loading.value);
  });

  async function fetchTemplates(params, initialLoading = false) {
    const baseUri = route('redesign.company.templates.load');
    const uri = params ? `${baseUri}?${params}` : baseUri;
    loading.value = initialLoading;
    try {
      const response = await axios.get(uri);
      templates.value = response.data;
      noValuesFound.value = response.data.length === 0;
    } catch (error) {
      toast.error(error.response?.data?.message || __('An unexpected error has occured. Please try again later.'));
    } finally {
      loading.value = false;
    }
  }

  async function handlePageChange(index) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', index);
    window.history.replaceState({}, '', url);

    await fetchTemplates(url.searchParams.toString());
  }

  const noValuesFound = ref(false);
  const isLoadingDelete = ref(false);
  const redirectToPage = (name, params) => {
    window.location.href = route(name, params);
  };

  const handleSearchInput = (value) => {
    clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(async () => {
      const url = new URL(window.location.href);

      if (!value) {
        url.searchParams.delete('search');
      } else {
        url.searchParams.set('search', value);
      }

      url.searchParams.delete('page');
      window.history.replaceState({}, '', url);

      await fetchTemplates(url.searchParams.toString());
    }, 500);
  };

  const handleTemplateEdit = (templateId) => {
    redirectToPage('redesign.company.templates.edit', templateId);
  };
  const handleTemplateDelete = async (templateId) => {
    isLoadingDelete.value = true;
    try {
      const response = await axios.delete(
        route('redesign.company.templates.destroy', templateId)
      );
      setTimeout(() => {
        toast.success(
          response?.data?.message || __('Template deleted successfully')
        );
        handlePageChange(1);
      }, 750);
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingDelete.value = false;
      openPopupMenuId.value = null;
    }
  };
  const handlePopupToggle = (templateId) => {
    openPopupMenuId.value =
      openPopupMenuId.value === templateId ? null : templateId;
  };
</script>
